import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { FileText, Plus, Trash2, FileSignature, ArrowRightLeft } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const TYPES = [
  { key: "all", label: "Tous" },
  { key: "devis", label: "Devis" },
  { key: "facture", label: "Factures" },
  { key: "bon_commande", label: "Bons de commande" },
  { key: "bon_livraison", label: "Bons de livraison" },
];

const TYPE_LABEL = {
  devis: "Devis", facture: "Facture", bon_commande: "Bon de commande", bon_livraison: "Bon de livraison",
};

const STATUS_STYLE = {
  brouillon: "bg-slate-100 text-slate-600",
  envoye: "bg-sky-50 text-sky-700",
  signe: "bg-emerald-50 text-emerald-700",
  paye: "bg-emerald-50 text-emerald-700",
  refuse: "bg-rose-50 text-rose-700",
};

export default function Documents() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/documents").then((r) => setDocs(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const shown = docs.filter((d) => filter === "all" || d.type === filter);

  const remove = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/documents/${id}`);
    toast.success("Document supprimé");
    load();
  };

  const convert = async (e, id, target) => {
    e.stopPropagation();
    const { data } = await api.post(`/documents/${id}/convert?target=${target}`);
    toast.success(`${TYPE_LABEL[target]} créé`);
    navigate(`/documents/${data.id}`);
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-display font-bold text-navy">Devis & Factures</h1>
          <p className="text-slate-500 mt-1">Devis, factures, bons de commande & de livraison · conformes PDP</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/documents/new/devis")} data-testid="create-devis-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-400 text-navy font-semibold hover:bg-sky-500 transition-colors">
            <Plus className="w-4 h-4" /> Devis
          </button>
          <button onClick={() => navigate("/documents/new/facture")} data-testid="create-facture-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white font-semibold hover:bg-navy-deep transition-colors">
            <Plus className="w-4 h-4" /> Facture
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TYPES.map((t) => (
          <button key={t.key} onClick={() => setFilter(t.key)} data-testid={`filter-${t.key}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              filter === t.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/60">
              <th className="px-5 py-3 font-medium">Numéro</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium hidden sm:table-cell">Client</th>
              <th className="px-5 py-3 font-medium">Statut</th>
              <th className="px-5 py-3 font-medium text-right">Total TTC</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((d) => (
              <tr key={d.id} onClick={() => navigate(`/documents/${d.id}`)}
                className="border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer"
                data-testid={`doc-row-${d.id}`}>
                <td className="px-5 py-3 font-mono text-navy">{d.number}</td>
                <td className="px-5 py-3">{TYPE_LABEL[d.type]}</td>
                <td className="px-5 py-3 hidden sm:table-cell text-slate-600">{d.client_name || "—"}</td>
                <td className="px-5 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[d.status] || "bg-slate-100 text-slate-600"}`}>
                    {d.signed ? "Signé" : d.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-right font-semibold tabular text-navy">{euro(d.total_ttc)}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {d.type === "devis" && (
                      <button onClick={(e) => convert(e, d.id, "facture")} title="Convertir en facture"
                        data-testid={`convert-${d.id}`}
                        className="p-1.5 rounded-lg hover:bg-sky-50 text-sky-600">
                        <ArrowRightLeft className="w-4 h-4" />
                      </button>
                    )}
                    <button onClick={(e) => remove(e, d.id)} title="Supprimer"
                      data-testid={`delete-${d.id}`}
                      className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && shown.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Aucun document. Créez votre premier devis.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
